import React, { useState, useEffect, useMemo } from "react";
import { getAllTeamMembers } from "../../services/azureTeamService";

// Dialog: GL/PL wählen aus, welche Team-Mitglieder DIESEN Terminplan bearbeiten dürfen.
// Speichert eine Liste [{uuid, teamId, name}] (siehe canEditTimeline / azureTimelinesService).
const TimelineEditorsModal = ({ timelineName, currentEditors, onSave, onClose }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set()); // Keys: employeeUuid || teamId

  const memberKey = (m) => m.employeeUuid || m.teamId;
  // Geschäftsleitung darf ohnehin immer bearbeiten -> im Dialog fix aktiviert (gesperrt).
  const isGL = (m) => m.kategorie === "GL";
  // Aktiv = aktuell angestellt (Eintritt erreicht, kein/erst künftiges Austrittsdatum).
  const today = new Date().toISOString().slice(0, 10);
  const isActiveMember = (m) =>
    (!m.start || m.start <= today) && (!m.ende || m.ende >= today);

  const isInCurrent = (m) =>
    (currentEditors || []).some(
      (e) =>
        (e.uuid && m.employeeUuid && e.uuid === m.employeeUuid) ||
        (e.teamId && m.teamId && e.teamId === m.teamId)
    );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const all = await getAllTeamMembers();
        if (cancelled) return;
        // Nur aktives internes Team (keine Freelancer:innen, keine ausgetretenen Personen).
        const team = all
          .filter((m) => m.imTeam !== false && m.kategorie !== "Freelancer" && isActiveMember(m))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setMembers(team);
        // Vorauswahl: bestehende Freigabe + Geschäftsleitung (immer aktiviert).
        const pre = new Set();
        team.forEach((m) => {
          if (isGL(m) || isInCurrent(m)) pre.add(memberKey(m));
        });
        setSelected(pre);
      } catch (err) {
        if (!cancelled) setError(err?.message || "Team konnte nicht geladen werden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // currentEditors ist beim Öffnen stabil; bewusst nur einmalig laden.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter((m) => (m.name || "").toLowerCase().includes(term));
  }, [members, search]);

  const toggle = (m) => {
    const key = memberKey(m);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    const list = members
      .filter((m) => selected.has(memberKey(m)))
      .map((m) => ({ uuid: m.employeeUuid || "", teamId: m.teamId || "", name: m.name || "" }));
    try {
      setSaving(true);
      await onSave(list);
    } catch (err) {
      setError(err?.message || "Speichern fehlgeschlagen.");
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content estimate-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Bearbeiter:innen festlegen</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <p style={{ marginBottom: "0.75rem", opacity: 0.75 }}>
            Wer darf den Terminplan <strong>{timelineName}</strong> bearbeiten? Geschäftsleitung
            und Projektleitung können ohnehin immer bearbeiten.
          </p>

          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading">Team wird geladen…</div>
          ) : (
            <>
              <div className="form-group">
                <input
                  type="text"
                  className="search-input"
                  placeholder="Person suchen…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div
                style={{
                  maxHeight: "320px",
                  overflowY: "auto",
                  border: "1px solid var(--border-color, #e5e5e5)",
                  borderRadius: "6px",
                }}
              >
                {filtered.length === 0 ? (
                  <div style={{ padding: "1rem", opacity: 0.6, textAlign: "center" }}>
                    Keine Person gefunden.
                  </div>
                ) : (
                  filtered.map((m) => {
                    const key = memberKey(m);
                    const locked = isGL(m); // GL ist fix aktiviert
                    return (
                      <label
                        key={key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.6rem",
                          padding: "0.5rem 0.75rem",
                          cursor: locked ? "default" : "pointer",
                          borderBottom: "1px solid var(--border-color, #f0f0f0)",
                          opacity: locked ? 0.85 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          disabled={locked}
                          onChange={() => toggle(m)}
                        />
                        <span>{m.name || "(ohne Name)"}</span>
                        {locked && (
                          <span style={{ fontSize: "0.75rem", opacity: 0.6 }}>
                            GL – immer aktiv
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
              <div style={{ marginTop: "0.5rem", fontSize: "0.85rem", opacity: 0.7 }}>
                {selected.size} {selected.size === 1 ? "Person" : "Personen"} ausgewählt
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimelineEditorsModal;
